import type { ComponentType } from "react";
import { createBrowserRouter, type RouteObject } from "react-router";
import { AppLoading } from "@/components/layout/app-loading";
import { AppShell } from "@/components/layout/app-shell";
import { RequireAuth } from "@/components/layout/guards";
import { RootLayout } from "@/components/layout/root-layout";
import { NotFound, RouteError } from "@/components/layout/route-error";
import { websiteOwns } from "@/routes/gated";

const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({
  Component: (await load()).default,
});

/**
 * The app's pages. Once the public website is a separate site, the home page (for people who are not signed in), the blog and the legal
 * addresses are handed to it: each of those pages decides that for itself (see routes/home.tsx and routes/gated.tsx), because a phone's
 * saved copy of the app cannot know it until it has asked the server.
 */
export const routes: RouteObject[] = [
  {
    errorElement: <RouteError />,
    hydrateFallbackElement: <AppLoading />,
    element: <RootLayout />,
    children: [
      { path: "/", lazy: page(() => import("@/routes/home")) },
      { path: "/sign-in", lazy: page(() => import("@/routes/auth/sign-in")) },
      { path: "/sign-up", lazy: page(() => import("@/routes/auth/sign-up")) },
      { path: "/forgot-password", lazy: page(() => import("@/routes/auth/forgot-password")) },
      { path: "/reset-password", lazy: page(() => import("@/routes/auth/reset-password")) },
      { path: "/join/:token", lazy: page(() => import("@/routes/join")) },
      { path: "/w/:code", lazy: page(() => import("@/routes/watch")) },
      { path: "/help", lazy: page(() => import("@/routes/help")) },
      {
        path: "/blog",
        lazy: websiteOwns(
          () => import("@/routes/blog/index"),
          () => "/blog/",
        ),
      },
      {
        path: "/blog/:slug",
        lazy: websiteOwns(
          () => import("@/routes/blog/post"),
          ({ slug }) => `/blog/${encodeURIComponent(slug ?? "")}/`,
        ),
      },
      {
        path: "/legal/terms",
        lazy: websiteOwns(
          () => import("@/routes/legal/terms"),
          () => "/legal/terms/",
        ),
      },
      {
        path: "/legal/privacy",
        lazy: websiteOwns(
          () => import("@/routes/legal/privacy"),
          () => "/legal/privacy/",
        ),
      },
      {
        path: "/app",
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, lazy: page(() => import("@/routes/app/dashboard")) },
              { path: "start", lazy: page(() => import("@/routes/app/start")) },
              { path: "games", lazy: page(() => import("@/routes/app/games")) },
              { path: "past", lazy: page(() => import("@/routes/app/past-boards")) },
              { path: "groups", lazy: page(() => import("@/routes/app/groups")) },
              { path: "groups/:groupId", lazy: page(() => import("@/routes/app/group")) },
              {
                path: "groups/:groupId/boards/new",
                lazy: page(() => import("@/routes/app/board-new")),
              },
              { path: "boards/:boardId", lazy: page(() => import("@/routes/app/board")) },
              {
                path: "boards/:boardId/import",
                lazy: page(() => import("@/routes/app/board-import")),
              },
              { path: "notifications", lazy: page(() => import("@/routes/app/notifications")) },
              { path: "settings", lazy: page(() => import("@/routes/app/settings")) },
              { path: "admin", lazy: page(() => import("@/routes/app/admin")) },
              { path: "admin/accounts", lazy: page(() => import("@/routes/app/admin-users")) },
              { path: "admin/support", lazy: page(() => import("@/routes/app/admin-support")) },
              { path: "admin/support/:id", lazy: page(() => import("@/routes/app/admin-ticket")) },
              { path: "admin/users/:id", lazy: page(() => import("@/routes/app/admin-user")) },
            ],
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
